// Barcode widget. Legacy: addBarcode/generateBarcodeSVG/updateBarcodeStyle/
// updateBarcodeValue (snapshot L4154-4299).
// Fabric v7 (map §4.2/§4.3):
//   - fabric.loadSVGFromString(str, cb) -> await loadSVGFromString(str) => { objects, ... }
//   - fabric.util.groupSVGElements REMOVED -> new Group(objects.filter(Boolean))
//     (default FitContentLayout sizes the group to its content, so group.width is
//      valid for the scale math below — no manual StaticLayout needed here)
import { Group, loadSVGFromString, type FabricObject } from 'fabric'
import JsBarcode from 'jsbarcode'
import { getCanvas } from './../state'
import { labelPx } from './../canvas'
import { toast } from './../ui/dom'

export interface BarcodeStyle {
  font?: string
  textPosition?: 'bottom' | 'top'
  fontSize?: number
  displayValue?: boolean
}

const DEFAULT_VALUES: Record<string, string> = {
  EAN13: '5901234123457', EAN8: '96385074', UPC: '012345678905', UPCE: '01234565',
  ITF14: '10012345000017', ITF: '123456', CODE93: 'ABCDEF', codabar: 'A12345B',
  MSI: '1234567', pharmacode: '1234',
}

export function defaultBarcodeValue(format: string): string {
  return DEFAULT_VALUES[format] ?? '12345678'
}

/** Human-readable input rule per format (shown when a value is rejected). */
export const BARCODE_RULES: Record<string, string> = {
  CODE128: 'any text', CODE39: 'A–Z, 0–9, space and - . $ / + %', CODE93: 'A–Z, 0–9, space and - . $ / + %',
  EAN13: '12 or 13 digits', EAN8: '7 or 8 digits', UPC: '11 or 12 digits', UPCE: '6, 7 or 8 digits',
  ITF14: '13 or 14 digits', ITF: 'an even number of digits', codabar: 'digits and - $ : / . +, optionally wrapped in A–D',
  MSI: 'digits only', pharmacode: 'a number from 3 to 131070',
}

/** True when JsBarcode can encode `value` in `format` (customer: EAN-13 rejected
 *  '{{sku}}' and non-numeric CSV values; Code 128 accepts anything). */
export function barcodeValueValid(format: string, value: string): boolean {
  if (!value) return false
  let ok = true
  const svgEl = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  try {
    JsBarcode(svgEl, value, { format, valid: (v: boolean) => { ok = v } } as Parameters<typeof JsBarcode>[2])
  } catch {
    ok = false
  }
  return ok
}

/** Render a barcode to an SVG string via JsBarcode (legacy generateBarcodeSVG). */
export function generateBarcodeSVG(format: string, value: string, style: BarcodeStyle = {}): string {
  const svgEl = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
  document.body.appendChild(svgEl)
  try {
    JsBarcode(svgEl, value, {
      format,
      width: 2,
      height: 60,
      displayValue: style.displayValue ?? true,
      font: style.font || 'monospace',
      fontSize: style.fontSize || 14,
      textPosition: style.textPosition || 'bottom',
      textAlign: 'center',
      margin: 5,
      background: '#ffffff',
    })
    return new XMLSerializer().serializeToString(svgEl)
  } finally {
    document.body.removeChild(svgEl)
  }
}

async function buildBarcodeGroup(format: string, value: string, style: BarcodeStyle): Promise<Group> {
  const svg = generateBarcodeSVG(format, value, style)
  const { objects } = await loadSVGFromString(svg)
  return new Group(objects.filter(Boolean) as FabricObject[])
}

const isPlaceholder = (v: string | undefined): boolean =>
  !v || v.trim() === '' || /^\{\{[^}]+\}\}$/.test(v)

export async function addBarcode(format = 'CODE128'): Promise<Group> {
  const value = defaultBarcodeValue(format)
  const group = await buildBarcodeGroup(format, value, {})
  const { w, h } = labelPx()
  const scale = Math.min((w * 0.7) / (group.width || 1), 1)
  group.set({
    left: w / 2 - (group.width * scale) / 2,
    top: h / 2 - (group.height * scale) / 2,
    scaleX: scale,
    scaleY: scale,
    id: 'barcode-' + Date.now(),
    barcodeFormat: format,
    barcodeValue: value,
  } as any)
  const c = getCanvas()
  c.add(group)
  c.setActiveObject(group)
  c.requestRenderAll()
  return group
}

/** Regenerate a barcode object in place, preserving transform + applying new value/style. */
export async function updateBarcode(
  obj: FabricObject,
  opts: { value?: string; style?: BarcodeStyle },
): Promise<Group | undefined> {
  const anyObj = obj as any
  if (anyObj.barcodeFormat == null) return
  const format: string = anyObj.barcodeFormat
  const value = opts.value !== undefined ? opts.value : (anyObj.barcodeValue ?? '')
  const style: BarcodeStyle = {
    font: opts.style?.font ?? anyObj.barcodeStyleFont ?? 'monospace',
    textPosition: opts.style?.textPosition ?? anyObj.barcodeStyleTextPos ?? 'bottom',
    fontSize: opts.style?.fontSize ?? anyObj.barcodeStyleFontSize ?? 14,
    displayValue: opts.style?.displayValue ?? anyObj.barcodeStyleShowText ?? true,
  }
  const pos = { left: obj.left, top: obj.top, scaleX: obj.scaleX, scaleY: obj.scaleY, angle: obj.angle, id: anyObj.id }

  // Placeholder ({{field}}) or invalid input: preview the format's sample value
  // (a literal '0000000000' was invalid for EAN/UPC/ITF and threw — the value
  // 'would not accept' the variable). The typed value is still stored so the
  // batch can substitute it; the preview is dimmed to signal it's not literal.
  const placeholder = isPlaceholder(value)
  const invalid = !placeholder && !barcodeValueValid(format, value)
  const display = placeholder || invalid ? defaultBarcodeValue(format) : value
  const group = await buildBarcodeGroup(format, display, style)
  group.set({
    ...pos,
    barcodeFormat: format,
    barcodeValue: value,
    barcodeStyleFont: style.font,
    barcodeStyleTextPos: style.textPosition,
    barcodeStyleFontSize: style.fontSize,
    barcodeStyleShowText: style.displayValue,
    opacity: placeholder || invalid ? 0.5 : 1,
    barcodeInvalid: invalid || undefined,
  } as any)

  const c = getCanvas()
  c.remove(obj)
  c.add(group)
  c.setActiveObject(group)
  c.requestRenderAll()
  if (invalid) toast(`Not a valid ${format} value — needs ${BARCODE_RULES[format] ?? 'a supported value'}`, 3500)
  return group
}
